import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Button } from '@spark/ui-components/components/Button';
import TanStackTable from '@spark/ui-components/components/TanStackTable/TanStackTable';
import FilterPanel from '@spark/ui-components/components/FilterPanel';
import { getModelos } from '@spark/services';
import { modeloVeicularFilterConfig } from '../config/modeloVeicularFilterConfig';

const ModeloVeicularSelectionModal = ({ show, onHide, onSelect }) => {
  const [loading, setLoading] = useState(false);
  const [modelos, setModelos] = useState([]);
  const [filtros, setFiltros] = useState({});
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [quantidadePorPagina, setQuantidadePorPagina] = useState(10);
  const [totalRegistros, setTotalRegistros] = useState(0);

  useEffect(() => {
    if (!show) {
      setFiltros({});
      setPaginaAtual(1);
      setQuantidadePorPagina(10);
      return;
    }
    setFiltros({});
    setPaginaAtual(1);
    setQuantidadePorPagina(10);
  }, [show]);

  const handleFiltrosChange = useCallback((novosFiltros) => {
    setFiltros(novosFiltros || {});
    setPaginaAtual(1);
  }, []);

  const carregarModelos = useCallback(async () => {
    if (!show) return;
    setLoading(true);
    try {
      const response = await getModelos({
        search: filtros?.search || undefined,
        quantidadePorPagina,
        pagina: paginaAtual
      });
      const registros = Array.isArray(response)
        ? response
        : response?.registros || response?.data || [];
      setModelos(registros);
      setTotalRegistros(
        response?.totalRegistros ??
        response?.total ??
        response?.totalItems ??
        registros.length
      );
    } catch (error) {
      console.error('Erro ao carregar modelos veiculares:', error);
      setModelos([]);
    } finally {
      setLoading(false);
    }
  }, [show, filtros, paginaAtual, quantidadePorPagina]);

  const handlePaginationChange = useCallback((paginationState) => {
    const nextPage = (paginationState.pageIndex ?? 0) + 1;
    const nextSize = paginationState.pageSize ?? quantidadePorPagina;
    if (nextSize !== quantidadePorPagina) {
      setQuantidadePorPagina(nextSize);
      setPaginaAtual(1);
      return;
    }
    if (nextPage !== paginaAtual) {
      setPaginaAtual(nextPage);
    }
  }, [paginaAtual, quantidadePorPagina]);

  useEffect(() => {
    carregarModelos();
  }, [carregarModelos]);

  const handleSelect = (modelo) => {
    const value = modelo.id ?? modelo.Id ?? '';
    const label =
      modelo.descricao ??
      modelo.Descricao ??
      modelo.nomeTipoModeloVeicular ??
      modelo.NomeTipoModeloVeicular ??
      String(value || '');
    onSelect({
      value: String(value),
      label
    });
    onHide();
  };

  const columns = useMemo(() => [
    {
      header: 'Descrição',
      accessorKey: 'descricao',
      cell: ({ row }) =>
        row.original.descricao ??
        row.original.Descricao ??
        row.original.nomeTipoModeloVeicular ??
        row.original.NomeTipoModeloVeicular ??
        '-'
    },

    {
      id: 'acoes',
      header: 'Ações',
      cell: ({ row }) => (
        <Button variant="primary" size="sm" onClick={() => handleSelect(row.original)}>
          Selecionar
        </Button>
      )
    }
  ], []);

  return (
    <Modal show={show} onHide={onHide} animation={false} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Selecionar Modelo Veicular</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <FilterPanel
          filters={modeloVeicularFilterConfig}
          onApply={handleFiltrosChange}
          title="Filtros de Busca"
          loading={loading}
          defaultCollapsed={false}
        />

        <TanStackTable
          key={`modelo-selection-${paginaAtual}-${quantidadePorPagina}`}
          columns={columns}
          data={modelos}
          loading={loading}
          withPagination={true}
          pageSizeOptions={[5, 10, 25, 50]}
          enableRowColors={true}
          className="mt-3"
          manualPagination={true}
          totalItems={totalRegistros}
          controlledPagination={{
            pageIndex: Math.max(0, (paginaAtual || 1) - 1),
            pageSize: quantidadePorPagina || 10
          }}
          onPaginationChange={handlePaginationChange}
        />
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div className="text-muted">
            {totalRegistros > 0 && (
              <span>
                Exibindo {modelos.length} de {totalRegistros} modelo(s)
              </span>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={onHide}>
              Cancelar
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ModeloVeicularSelectionModal;
